import React, { useRef, useState, useEffect } from 'react'
import { Box, Typography, Portal } from '@mui/material'
import { MagnifyingGlassIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import { COUNTRIES, PINNED_COUNTRIES, CountryOption } from '../../../../config/demographicVariables'

interface DemographicCountrySelectProps {
  value: number | null
  onChange: (value: number | null) => void
}

/**
 * Country picker matching the design:
 * - Always-visible search input + quick-select pills (Sweden / Denmark / Germany)
 * - Typing shows a dropdown of filtered results and hides the pills
 * - Keyboard: ↑↓ + Enter to pick, Escape clears search
 */
export const DemographicCountrySelect: React.FC<DemographicCountrySelectProps> = ({ value, onChange }) => {
  const [query, setQuery] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })

  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const selected = COUNTRIES.find((c) => c.value === value) ?? null
  const isTyping = query.trim().length > 0

  const filteredOptions: CountryOption[] = isTyping
    ? COUNTRIES.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()))
    : COUNTRIES

  // Recompute dropdown anchor whenever it opens
  const updateDropdownPos = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect()
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }

  // Open dropdown when user starts typing
  useEffect(() => {
    if (isTyping) {
      updateDropdownPos()
      setDropdownOpen(true)
      setHighlightedIndex(0)
    }
  }, [isTyping])

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current?.contains(e.target as Node) || listRef.current?.contains(e.target as Node)) return
      setDropdownOpen(false)
      setQuery('')
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dropdownOpen])

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return
    const item = listRef.current.querySelector<HTMLElement>(`[data-idx="${highlightedIndex}"]`)
    item?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex])

  const selectCountry = (option: CountryOption) => {
    onChange(option.value)
    setQuery('')
    setDropdownOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!dropdownOpen) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.min(i + 1, filteredOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filteredOptions[highlightedIndex]) selectCountry(filteredOptions[highlightedIndex])
    } else if (e.key === 'Escape') {
      setQuery('')
      setDropdownOpen(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Search input */}
      <Box
        ref={wrapperRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          height: 44,
          borderRadius: '8px',
          border: dropdownOpen ? `1px solid ${INTERAX_COLORS.thunderGrey}` : `1px solid rgba(255,255,255,0.12)`,
          backgroundColor: 'rgba(255,255,255,0.04)',
          transition: 'border-color 0.15s'
        }}
      >
        <MagnifyingGlassIcon style={{ width: 16, height: 16, color: INTERAX_COLORS.thunderGrey, flexShrink: 0 }} />

        <Box
          ref={inputRef}
          component="input"
          value={isTyping ? query : selected ? selected.label : query}
          placeholder={selected ? '' : 'Search countries...'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (selected) setQuery('')
            updateDropdownPos()
            setDropdownOpen(true)
            setHighlightedIndex(0)
          }}
          sx={{
            flex: 1,
            background: 'none',
            border: 'none',
            outline: 'none',
            color: INTERAX_COLORS.white,
            fontSize: '14px',
            fontFamily: 'inherit',
            '&::placeholder': { color: INTERAX_COLORS.thunderGrey }
          }}
        />

        {selected && !isTyping && (
          <Box
            onClick={() => {
              onChange(null)
              setQuery('')
            }}
            sx={{
              cursor: 'pointer',
              color: INTERAX_COLORS.thunderGrey,
              display: 'flex',
              '&:hover': { color: INTERAX_COLORS.white }
            }}
          >
            <XMarkIcon style={{ width: 14, height: 14 }} />
          </Box>
        )}
      </Box>

      {/* Search results dropdown */}
      {dropdownOpen && (
        <Portal>
          <Box
            ref={listRef}
            sx={{
              position: 'absolute',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 99999,
              backgroundColor: INTERAX_COLORS.heritageBlue,
              border: `1px solid rgba(255,255,255,0.1)`,
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              maxHeight: 300,
              overflowY: 'auto'
            }}
          >
            {/* Quick-select pinned countries */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography
                sx={{
                  ...INTERAX_TYPOGRAPHY.body3,
                  color: INTERAX_COLORS.thunderGrey,
                  fontWeight: 700,
                  fontSize: '10px',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  mb: 1
                }}
              >
                Quick select
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {PINNED_COUNTRIES.map((country) => {
                  const isSelected = country.value === value
                  return (
                    <Box
                      key={country.value}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        if (isSelected) {
                          onChange(null)
                        } else {
                          selectCountry(country)
                        }
                      }}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.75,
                        py: 0.625,
                        borderRadius: '999px',
                        border: isSelected ? `1.5px solid ${INTERAX_COLORS.sun}` : `1px solid rgba(255,255,255,0.2)`,
                        backgroundColor: isSelected ? INTERAX_COLORS.sun10 : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': {
                          borderColor: INTERAX_COLORS.sun40,
                          backgroundColor: INTERAX_COLORS.sun10
                        }
                      }}
                    >
                      <MapPinIcon
                        style={{
                          width: 12,
                          height: 12,
                          color: isSelected ? INTERAX_COLORS.sun : INTERAX_COLORS.thunderGrey,
                          flexShrink: 0
                        }}
                      />
                      <Typography
                        sx={{
                          ...INTERAX_TYPOGRAPHY.body3,
                          color: isSelected ? INTERAX_COLORS.sun : INTERAX_COLORS.white,
                          fontWeight: isSelected ? 600 : 400,
                          fontSize: '13px'
                        }}
                      >
                        {country.label}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            </Box>

            {/* Divider */}
            <Box sx={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.08)', mx: 0 }} />

            {/* Country list */}
            {filteredOptions.length === 0 ? (
              <Typography sx={{ ...INTERAX_TYPOGRAPHY.body3, color: INTERAX_COLORS.thunderGrey, px: 2, py: 1.5 }}>
                No countries match
              </Typography>
            ) : (
              filteredOptions.map((option, idx) => {
                const isHighlighted = idx === highlightedIndex
                const isSelected = option.value === value
                return (
                  <Box
                    key={option.value}
                    data-idx={idx}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      selectCountry(option)
                    }}
                    sx={{
                      px: 2,
                      py: 0.875,
                      cursor: 'pointer',
                      backgroundColor: isHighlighted
                        ? 'rgba(255,255,255,0.08)'
                        : isSelected
                          ? INTERAX_COLORS.sun10
                          : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography
                      sx={{
                        ...INTERAX_TYPOGRAPHY.body3,
                        color: isSelected ? INTERAX_COLORS.sun : INTERAX_COLORS.white,
                        fontWeight: isSelected ? 600 : 400
                      }}
                    >
                      {option.label}
                    </Typography>
                    {isSelected && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: INTERAX_COLORS.sun,
                          flexShrink: 0
                        }}
                      />
                    )}
                  </Box>
                )
              })
            )}
          </Box>
        </Portal>
      )}
    </Box>
  )
}
